import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Settings } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
} from "@tabipla/ui";
import { useUpdateGroup } from "../../../application/hooks/useGroups";
import { GroupIconPicker } from "../groups/GroupIconPicker";
import { GroupImageUploader } from "../groups/GroupImageUploader";

interface GroupData {
  name: string;
  description: string | null;
  iconEmoji: string | null;
  iconColor: string | null;
  iconImageUrl: string | null;
  coverImageUrl: string | null;
  backgroundImageUrl: string | null;
  backgroundColor: string | null;
}

interface GroupSettingsDialogProps {
  groupId: string;
  group: GroupData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const GRADIENT_COLORS = [
  "from-blue-50 to-indigo-100",
  "from-purple-50 to-pink-100",
  "from-emerald-50 to-teal-100",
  "from-amber-50 to-orange-100",
];

export function GroupSettingsDialog({
  groupId,
  group,
  open,
  onOpenChange,
}: GroupSettingsDialogProps) {
  const { t } = useTranslation();

  // Basic info
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? "");

  // Icon fields
  const [iconEmoji, setIconEmoji] = useState<string | null>(group.iconEmoji);
  const [iconColor, setIconColor] = useState<string | null>(group.iconColor);
  const [iconImageUrl, setIconImageUrl] = useState<string | null>(group.iconImageUrl);
  const [iconImageKey, setIconImageKey] = useState<string | null>(null);

  // Cover image
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(group.coverImageUrl);
  const [coverImageKey, setCoverImageKey] = useState<string | null>(null);

  // Background
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(
    group.backgroundImageUrl
  );
  const [backgroundImageKey, setBackgroundImageKey] = useState<string | null>(null);
  const [backgroundColor, setBackgroundColor] = useState<string | null>(group.backgroundColor);

  const updateGroupMutation = useUpdateGroup(groupId);

  // Sync with props when dialog opens
  useEffect(() => {
    if (open) {
      setName(group.name);
      setDescription(group.description ?? "");
      setIconEmoji(group.iconEmoji);
      setIconColor(group.iconColor);
      setIconImageUrl(group.iconImageUrl);
      setIconImageKey(null);
      setCoverImageUrl(group.coverImageUrl);
      setCoverImageKey(null);
      setBackgroundImageUrl(group.backgroundImageUrl);
      setBackgroundImageKey(null);
      setBackgroundColor(group.backgroundColor);
    }
  }, [open, group]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateGroupMutation.mutateAsync({
        name,
        description: description || null,
        iconEmoji,
        iconColor,
        iconImageUrl,
        iconImageKey,
        coverImageUrl,
        coverImageKey,
        backgroundImageUrl,
        backgroundImageKey,
        backgroundColor,
      });
      toast.success(t("groups.updateSuccess", "グループを更新しました"));
      onOpenChange(false);
    } catch {
      toast.error(t("groups.updateError", "更新に失敗しました"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-primary" />
            {t("groups.settingsTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("groups.settingsDescription", "グループの設定を変更します")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("groups.name")}</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("groups.namePlaceholder")}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("groups.description")}
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({t("common.optional")})
                  </span>
                </label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("groups.descriptionPlaceholder")}
                />
              </div>
            </div>

            <div className="border-t" />

            {/* Icon Picker */}
            <GroupIconPicker
              iconEmoji={iconEmoji}
              iconColor={iconColor}
              iconImageUrl={iconImageUrl}
              iconImageKey={iconImageKey}
              onEmojiChange={(emoji, color) => {
                setIconEmoji(emoji);
                setIconColor(color);
              }}
              onImageChange={(url, key) => {
                setIconImageUrl(url);
                setIconImageKey(key);
              }}
              groupName={name}
            />

            <div className="border-t" />

            {/* Cover Image */}
            <GroupImageUploader
              imageUrl={coverImageUrl}
              imageType="group_cover"
              onUpload={(url, key) => {
                setCoverImageUrl(url);
                setCoverImageKey(key);
              }}
              onRemove={() => {
                setCoverImageUrl(null);
                setCoverImageKey(null);
              }}
              label={t("groups.coverImage", "カバー画像")}
              shape="banner"
            />

            <div className="border-t" />

            {/* Background */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("groups.background", "背景")}</label>
              <GroupImageUploader
                imageUrl={backgroundImageUrl}
                imageType="group_background"
                onUpload={(url, key) => {
                  setBackgroundImageUrl(url);
                  setBackgroundImageKey(key);
                  setBackgroundColor(null);
                }}
                onRemove={() => {
                  setBackgroundImageUrl(null);
                  setBackgroundImageKey(null);
                }}
                shape="banner"
              />
              <p className="text-center text-xs text-muted-foreground">
                {t("groups.backgroundHint", "または、グラデーションカラーを選択")}
              </p>
              <div className="grid grid-cols-4 gap-2">
                {GRADIENT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      setBackgroundColor(color);
                      setBackgroundImageUrl(null);
                      setBackgroundImageKey(null);
                    }}
                    className={`h-10 rounded-lg bg-gradient-to-br ${color} transition-transform hover:scale-105 active:scale-95 ${
                      backgroundColor === color && !backgroundImageUrl
                        ? "ring-2 ring-primary ring-offset-2"
                        : ""
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateGroupMutation.isPending || !name.trim()}
              className="flex-1 sm:flex-none"
            >
              {updateGroupMutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
